<template>
  <DashboardContentLayout>
    <template
      v-if="!isLoading"
      #title>
      <span class="text-32">
        {{ fullName || $t('pages.onboarding.admin_page.not_specified') }}
      </span>
    </template>
    <template
      v-if="!isLoading"
      #title-actions>
      <RiskLevelDropdown
        :risk-level="store.riskLevel"
        :application-status="store.applicationStatus"
        :change-handler="store.updateRiskLevel"/>
    </template>
    <template
      v-if="!isLoading"
      #actions>
      <StatusDropdown
        v-if="store.blocked && store.applicationStatus !== 'CLOSED'"
        application-status="BLOCKED"
        :sections="[]"
        :verification-in-progress="false"/>
      <StatusDropdown
        :application-status="store.applicationStatus"
        :sections="uncompletedSections"
        :completed-all-user-stages="completedAllUserStages"
        @on-status-change="onApplicationStatusChange"
        @on-reject="declineApplication"/>
    </template>
    <template v-if="!isLoading">
      <TabLinks
        :tabs="tabs"
        secondary/>
    </template>
    <router-view v-slot="{ Component }">
      <Transition name="page">
        <template v-if="!isLoading">
          <component :is="Component"/>
        </template>
        <template v-else>
          <LoaderStub class="items-center"/>
        </template>
      </Transition>
    </router-view>
  </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { successNotification } from '@/_redesign/shared/ui/NotificationPopup/notification-popup';
import LoaderStub from '@/components/loader-stub.vue';
import TabLinks from '@/components/ui-kit/app-tab-links.vue';
import DashboardContentLayout from '@/layouts/dashboard/dashboard-content-layout.vue';
import RiskLevelDropdown from '@/modules/onboarding-dashboard/shared/components/RiskLevelDropdown.vue';
import StatusDropdown from '@/modules/onboarding-dashboard/shared/components/StatusDropdown.vue';
import { useCustomerProfileProvide } from '@/modules/onboarding-dashboard/shared/utils';
import { ONBOARDING_SOLE_TRADER_PROFILE_CHILDREN as tabs } from '@/modules/onboarding-dashboard/SoleTrader/routes';
import { requestHandler } from '@/services/requestHandler';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import {
  useOnboardingAdminSoleTraderStore,
} from '@/stores/onboarding/OnboardingAdminSoleTrader';
import { pollerFunction } from '@/utils/pollerFunction';

const store = useOnboardingAdminSoleTraderStore();

const { t } = useI18n();

const isLoading = ref(true);
const id = useRoute().params.id as string;
const declineSuccess = t(
  'pages.onboarding.business.company_details.decline.tooltip.success',
  { customer: 'customer' },
);

const fullName = computed(() => {
  if (!store.personalDetails?.firstName && !store.personalDetails?.lastName) {
    return '';
  }

  return [
    store.personalDetails?.firstName,
    store.personalDetails?.lastName,
  ].join(' ');
});

useCustomerProfileProvide('SOLE_TRADER');

const uncompletedSections = computed(() => {
  // DEBT: add check for other sections
  const sections: string[] = [];

  const stages = [
    { key: 'SOLE_TRADER_DETAILS', label: 'pages.onboarding.admin_page.personal_information' },
    { key: 'PROOF_OF_IDENTITY', label: 'pages.onboarding.admin_page.proof_of_identity' },
    { key: 'PROOF_OF_ADDRESS', label: 'pages.onboarding.admin_page.proof_of_address' },
    { key: 'RISK_LEVEL', label: 'pages.onboarding.admin_page.risk_level' },
    { key: 'CONTRACT', label: 'pages.onboarding.admin_page.plan' },
  ] as const;

  stages.forEach((stage) => {
    if (store.completedStages.includes(stage.key)) return;
    sections.push(t(stage.label));
  });

  return sections;
});

const completedAllUserStages = computed(() => store.completedStages.includes('PRICING'));

function checkVerificationStatus(limit = 4) {
  if (store.applicationStatus !== 'VERIFYING') return;

  const exitCondition = () => store.applicationStatus === 'VERIFIED';
  pollerFunction(() => store.fetchApplication(id, true), exitCondition, 0, limit)
    .then(() => {
      successNotification(
        t(
          'pages.onboarding.business.company_details.verify.tooltip.success',
          { customer: 'customer' },
        ),
      );
    });
}

async function readUserApplication() {
  isLoading.value = true;
  store.$reset();
  await store.fetchApplication(id, true);
  isLoading.value = false;

  checkVerificationStatus();
}

type Status = Exclude<Onboarding.UpdateApplicationStatus, 'decline' | 'verify'> | 'verified';

async function changeStatus(status: Exclude<Status, 'verified'>) {
  await requestHandler(() => store.updateStatus(status), {
    pending: isLoading,
  });
}

async function verifyApplication() {
  await requestHandler(() => store.verifyApplication(), {
    pending: isLoading,
    onSuccess: () => checkVerificationStatus(),
  });
}

async function onApplicationStatusChange(status: Status) {
  const isVerified = status === 'verified';

  if (isVerified) {
    return verifyApplication();
  }

  return changeStatus(status);
}

async function declineApplication() {
  await requestHandler(() => store.updateStatus('decline'), {
    pending: isLoading,
    successNotification: declineSuccess, // @TODO requestHandler syntax error
    onSuccess: () => checkVerificationStatus(),
  });
}

onBeforeMount(readUserApplication);
</script>
